/**
 * How a composition's timeline is mapped onto its scene.
 *
 * `absolute` treats an authored second as a scene second, so a tween positioned at 6.2 plays at 6.2s
 * of the scene no matter what else the document animates. `normalized` maps the scene across the
 * timeline's own total duration, which lets a graphic stretch to fill a resized scene but makes the
 * authoring unit emergent — any tween (notably a repeating one) can move it, dragging every other
 * tween's real time with it.
 */
export type HtmlTimeMode = 'absolute' | 'normalized';

export interface SeekTarget {
    /** `progress` seeks by normalized 0..1; `totalTime` seeks by absolute seconds. */
    kind: 'progress' | 'totalTime';
    value: number;
}

/**
 * Resolve a normalized scene progress into the seek the renderer should perform. Falls back to
 * `progress` whenever the scene duration can't be trusted, so a missing or nonsense duration
 * degrades to the stretch-to-fit behaviour rather than seeking to NaN.
 */
export function resolveSeekTarget(
    mode: HtmlTimeMode,
    progress: number,
    sceneDurationMs: number,
): SeekTarget {
    const clamped = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0));
    const usableDuration = Number.isFinite(sceneDurationMs) && sceneDurationMs > 0;
    if (mode === 'normalized' || !usableDuration) {
        return { kind: 'progress', value: clamped };
    }
    return { kind: 'totalTime', value: (clamped * sceneDurationMs) / 1000 };
}
